import type { OnboardingDraftItem, OnboardingRun } from '@hostesa/api-client';
import type { StatusVariant } from '@hostesa/ui';

/** Coerce an `unknown` (nullable) field to a display string, or null. */
export function asText(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value.length > 0 ? value : null;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

/** Coerce an `unknown` (nullable) field to a finite number, or null. */
export function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return null;
}

/** Render an extracted/reviewed draft value (object or scalar) as text. */
export function describeValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value.length > 0 ? value : null;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

/** Which wizard step a run is in: connect (0), review (1), activate (2). */
export function runStep(status: OnboardingRun['status']): 0 | 1 | 2 {
  switch (status) {
    case 'created':
    case 'url_validated':
    case 'crawling':
    case 'failed':
      return 0;
    case 'draft_ready':
    case 'in_review':
      return 1;
    default:
      // approved, indexing, validating, ready_for_test, activated, rejected, expired
      return 2;
  }
}

/** Fraction (0–1) of required draft items that have been reviewed. */
export function blockingItemsRemaining(items: readonly OnboardingDraftItem[]): number {
  return items.filter((i) => i.requiredField && i.status === 'pending_review').length;
}

/** A coarse activation progress fraction derived from run counters. */
export function activationProgress(run: OnboardingRun): number {
  if (run.status === 'activated') return 1;
  const total = run.itemsExtracted;
  if (total <= 0) return run.domainOwnershipConfirmed ? 0.1 : 0;
  return Math.min(1, run.itemsApproved / total);
}

export function runStatusVariant(status: OnboardingRun['status']): StatusVariant {
  switch (status) {
    case 'activated':
      return 'success';
    case 'failed':
    case 'rejected':
    case 'expired':
      return 'error';
    case 'crawling':
    case 'indexing':
    case 'validating':
      return 'pending';
    default:
      return 'neutral';
  }
}

export function itemStatusVariant(status: OnboardingDraftItem['status']): StatusVariant {
  switch (status) {
    case 'accepted':
    case 'edited':
      return 'success';
    case 'rejected':
      return 'inactive';
    default:
      return 'warning';
  }
}
